import { splitGenerationDisclaimer } from '@util/fitment/generationNotice';
import {
  Elements,
  PaymentMethodMessagingElement,
} from '@stripe/react-stripe-js';
import { useQuery } from '@tanstack/react-query';
import Avatar from '@ui/Avatar';
import { BreadcrumbItem } from '@ui/Breadcrumb';
import Button from '@ui/Button';
import { getProductViews } from '@util/firestore/products';
import { isDomestic } from '@util/firestore/shipengine';
import { getDefaultAddress } from '@util/firestore/users';
import { getStripeAmount } from '@util/get-stripe';
import { formatRange, getCountryLabelFromCode } from '@util/index';
import ProductPolicyAccordion from 'app/product/[slug]/components/ProductPolicyAccordion';
import VehicleSpecificPrompt from 'app/product/[slug]/components/VehicleSpecificPrompt';
import { useWarehouse } from 'app/product/[slug]/components/WarehouseProvider';
import { MX_DIRECT_SELLER_ID } from 'app/product/utils/vehicleSpecificDirect';
import { useAuth } from 'context/AuthContext';
import { useChat } from 'context/ChatContext';
import { useStripeContext } from 'context/StripeContext';
import dayjs from 'dayjs';
import { FlagIcon } from 'lucide-react';
import type { ProductDocument, Variation } from 'models/product';
import type { PublicUserDocument } from 'models/user';
import Image from 'next/image';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import { EyeIcon } from './icons';
import ReportItemModal from './modals/ReportItemModal';
import ActionButtonsSkeleton from './product-details/ActionButtonsSkeleton';
import AuctionActions from './product-details/AuctionActions';
import BuyerActionButtons from './product-details/BuyerActionButtons';
import ProductDetailsImages from './product-details/ProductDetailsImages';
import ProductPriceAndShipping from './product-details/ProductPriceAndShipping';
import ProductVariantSelector from './product-details/ProductVariantSelector';
import SellerActionButtons from './product-details/SellerActionButtons';
import SellerProductOrders from './product-details/SellerProductOrders';
import SoldOrDraftBlock from './product-details/SoldOrDraftBlock';
import ProductConditions from './ProductConditions';
import ProductWarehouseSelector from './ProductWarehouseSelector';
interface ProductDetailPageProps {
  product: ProductDocument;
  seller: PublicUserDocument | null;
  breadcrumbs: BreadcrumbItem[];
  isPreview?: boolean;
  selectedVariations: Variation[] | null;
  onSelection: (v: Variation, isNumber: boolean, isColor: boolean) => void;
  setErrors: React.Dispatch<React.SetStateAction<string[]>>;
  onWarehouseSelection: (item: ProductDocument) => void;
}
export const ProductDetails = ({
  product,
  isPreview,
  breadcrumbs,
  seller,
  selectedVariations,
  onSelection,
  setErrors,
  onWarehouseSelection,
}: ProductDetailPageProps) => {
  const { userDoc, user } = useAuth();
  const { mainDisclaimer } = splitGenerationDisclaimer(product.disclaimer);
  const [customInputsEnabled, setCustomInputsEnabled] = useState(false);
  const [reportProductModalOpen, setReportProductModalOpen] = useState(false);
  const { openChatModalWithRecipientAndProductId } = useChat();
  const { isActive, mode, useGroupTitle } = useWarehouse();
  const inGeneralState = isActive && mode === 'general';
  // Bike match (specific mode) always shows MPN. Direct/shared URLs show it
  // before a vehicle is selected; card browse with group title waits for a match.
  const showMpn =
    !!product.mpn?.length &&
    (!isActive || mode === 'specific' || !useGroupTitle);

  const calculatePositivePercentage = () => {
    return seller?.rating ? Math.round((seller.rating / 5) * 100) : 0;
  };

  const { stripePromise } = useStripeContext();

  const { data: shipping = null } = useQuery({
    queryKey: ['shipping', product.id, userDoc?.uid ?? ''],
    queryFn: async () => {
      const getShipping = async () => {
        const isLoggedInAndHasAddress = !!userDoc && userDoc.addresses.length;
        // Not logged in or no address
        if (!isLoggedInAndHasAddress) {
          return product.is_flat_rate
            ? product.shipping_costs?.find((s) => s.code === 'US') ??
                product.shipping_costs?.[0] ??
                null
            : null;
        }

        const address = getDefaultAddress(userDoc);
        if (!address) return null;
        // Flat rate shipping
        if (product.is_flat_rate && product.shipping_costs?.length) {
          const foundShipping = product.shipping_costs.find(
            (s) => s.code === address.country_code
          );
          if (foundShipping) return foundShipping;
          const foundOther = product.shipping_costs.find(
            (s) => s.code === 'OT'
          );
          if (foundOther) return foundOther;
          return null;
        }

        // Domestic flat rate shipping
        if (isDomestic(product.shipment, address)) {
          return {
            cost: product.shipment.domestic_flat_rate,
            name: 'Continental US',
            code: 'US',
            estimated: true,
          };
        }

        return null;
      };
      return getShipping();
    },
  });

  const { data: views } = useQuery({
    queryKey: ['views', product.id],
    queryFn: () => getProductViews(product.id),
    enabled: !!product.id,
  });

  const onCustomInputsChange = (enabled: boolean) => {
    setCustomInputsEnabled(enabled);
  };

  // Calculate default delivery dates (3-10 business days for US users)
  const shippingDates = useMemo(() => {
    const addBusinessDays = (date: dayjs.Dayjs, days: number) => {
      let result = date;
      let addedDays = 0;
      while (addedDays < days) {
        result = result.add(1, 'day');
        if (result.day() !== 0 && result.day() !== 6) {
          // Skip weekends
          addedDays++;
        }
      }
      return result;
    };

    const isDirect = product.seller_id === MX_DIRECT_SELLER_ID;
    const today = dayjs();
    const minDays = isDirect ? 2 : 3;
    const maxDays = isDirect ? 3 : 10;
    return {
      earliest: addBusinessDays(today, minDays).toISOString(),
      latest: addBusinessDays(today, maxDays).toISOString(),
    };
  }, [product.seller_id]);

  const hideOffer =
    !!product.custom_inputs ||
    product.category === 'Giveaways' ||
    !!seller?.offers_disabled ||
    product.hide_offer === true;

  return (
    <div className="flex w-full flex-col gap-y-8 sm:w-auto">
      <div
        className={`flex w-full max-w-default flex-col justify-center gap-[3.2rem] sm:gap-[4.8rem] lg:flex-row `}
      >
        <div>
          <ProductDetailsImages
            product={product}
            selectedVariations={selectedVariations}
          />
        </div>

        <div className="flex w-full max-w-[60rem] flex-col gap-[1.6rem] px-8 sm:gap-[1.6rem] sm:px-4 lg:pt-[.5rem]">
          <div className="flex w-full items-center justify-between gap-x-8">
            <div className="flex flex-col gap-y-2">
              <h1 className="text-[2.5rem] font-bold leading-[3.25rem] text-black sm:w-[50rem] sm:text-[2.5rem]">
                {product.title}
              </h1>
              {showMpn && product.mpn?.[0] ? (
                <span className="pt-2 text-[1.4rem] font-medium text-gray-700">
                  <strong>MPN:</strong> {product.mpn[0]}
                </span>
              ) : null}
            </div>

            <div className="hidden items-center gap-x-2">
              <EyeIcon className="h-10 w-10 text-[#7F7F7F]" />

              <span className={'font-semibold text-zinc-800'}>
                {views?.views ?? 0}
              </span>
            </div>
          </div>

          {seller && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-x-4 border-y border-zinc-200 py-6">
                <Link
                  href={`/profile/${seller.username_slug}`}
                  className="relative shrink-0 self-start"
                >
                  <Avatar user={seller} size="small" />
                  {seller.is_verified && (
                    <div className="absolute -bottom-1 -right-1 flex items-center justify-center">
                      <svg
                        width="32"
                        height="34"
                        viewBox="0 0 20 21"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-8 w-8"
                      >
                        <path
                          d="M18.7134 4.85001C18.7046 4.71039 18.6491 4.57777 18.556 4.47338C18.4629 4.36899 18.3374 4.29886 18.1997 4.27422C14.8208 3.66797 13.4103 3.22969 10.2571 1.80547C10.1762 1.76891 10.0885 1.75 9.99971 1.75C9.91094 1.75 9.82318 1.76891 9.74229 1.80547C6.58917 3.22969 5.17862 3.66797 1.79971 4.27422C1.662 4.29886 1.53654 4.36899 1.44341 4.47338C1.35028 4.57777 1.29487 4.71039 1.28604 4.85001C1.13565 7.23711 1.45635 9.46133 2.24034 11.4609C2.88234 13.092 3.83245 14.5841 5.03877 15.8559C7.12745 18.0719 9.34503 19.0344 9.76807 19.2047C9.9184 19.2655 10.0865 19.2655 10.2368 19.2047C10.6599 19.0344 12.8774 18.0719 14.9661 15.8559C16.1705 14.5837 17.1187 13.0916 17.7591 11.4609C18.5431 9.46133 18.8638 7.23711 18.7134 4.85001Z"
                          fill="#1E7A1E"
                        />
                        <path
                          d="M13.5962 7.78399L9.26807 12.784C9.21242 12.8485 9.14418 12.9009 9.06753 12.938C8.99089 12.9752 8.90746 12.9963 8.82237 13H8.79659C8.63301 13 8.47594 12.936 8.35909 12.8215L6.43721 10.9387C6.37858 10.8812 6.33184 10.8128 6.29965 10.7373C6.26747 10.6617 6.25047 10.5806 6.24964 10.4985C6.24796 10.3328 6.3122 10.1731 6.42823 10.0547C6.54426 9.93628 6.70258 9.86881 6.86836 9.86712C7.03414 9.86544 7.1938 9.92968 7.31221 10.0457L8.75752 11.4633L12.6521 6.96602C12.7605 6.84066 12.9143 6.76353 13.0797 6.75159C13.245 6.73965 13.4083 6.79388 13.5337 6.90235C13.6591 7.01082 13.7362 7.16464 13.7481 7.32998C13.7601 7.49532 13.7058 7.65863 13.5974 7.78399H13.5962Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                  )}
                </Link>
                <div className="flex min-w-0 flex-1 flex-col gap-y-2">
                  <div className="flex min-w-0 flex-wrap items-center gap-x-2">
                    <Link
                      href={`/profile/${seller.username_slug}`}
                      className="text-[1.6rem] font-bold text-brand-secondary hover:underline"
                    >
                      {seller.username}
                    </Link>
                    {seller.reviews > 0 && (
                      <span className="shrink-0 whitespace-nowrap text-[1.6rem] font-medium text-zinc-600">
                        ({seller.reviews}{' '}
                        {seller.reviews === 1 ? 'Review' : 'Reviews'})
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    {seller.reviews > 0 &&
                      Boolean(calculatePositivePercentage()) && (
                        <div className="flex items-center gap-x-4">
                          <span className="whitespace-nowrap text-[1.3rem] font-semibold text-brand-black">
                            {calculatePositivePercentage()}% positive feedback
                          </span>
                          <div className="h-1 w-1 rounded-full bg-black" />
                        </div>
                      )}
                    <Link
                      href={`/profile/${seller.username_slug}`}
                      className="whitespace-nowrap text-[1.3rem] font-semibold text-brand-black underline"
                    >
                      View Profile
                    </Link>
                  </div>
                </div>

                <Button
                  type="outline"
                  className="ml-auto shrink-0 self-start rounded-full !text-[1.4rem] !font-bold"
                  onClick={() =>
                    openChatModalWithRecipientAndProductId({
                      recipientUid: seller.uid ?? '',
                      productId: product.id,
                    })
                  }
                >
                  Message
                </Button>
              </div>
            </div>
          )}

          {inGeneralState ? (
            <VehicleSpecificPrompt breadcrumbs={breadcrumbs} />
          ) : (
            <div className="mt-2 flex flex-col items-start gap-[1.6rem] xl:flex-row">
              <ProductPriceAndShipping
                product={product}
                selectedVariations={selectedVariations}
                shipping={shipping}
                customInputsEnabled={customInputsEnabled}
              />
            </div>
          )}

          {!inGeneralState &&
            !product.out_of_stock &&
            !product.is_draft &&
            !product.is_auction &&
            [
              'US',
              'CA',
              'AU',
              'NZ',
              'GB',
              'IE',
              'FR',
              'ES',
              'DE',
              'AT',
              'BE',
              'DK',
              'FI',
              'IT',
              'NL',
              'NO',
              'SE',
            ].includes(userDoc?.country.code ?? 'US') && (
              <Elements stripe={stripePromise}>
                <PaymentMethodMessagingElement
                  options={{
                    amount: getStripeAmount(
                      product.price + (shipping?.cost ?? 0)
                    ),
                    currency: 'USD',
                    paymentMethodTypes: ['affirm'],
                    countryCode: (userDoc?.country.code ?? 'US') as
                      | 'AT'
                      | 'AU'
                      | 'BE'
                      | 'CA'
                      | 'DE'
                      | 'DK'
                      | 'ES'
                      | 'FI'
                      | 'FR'
                      | 'GB'
                      | 'IE'
                      | 'IT'
                      | 'NL'
                      | 'NO'
                      | 'NZ'
                      | 'SE'
                      | 'US',
                  }}
                />
              </Elements>
            )}

          <ProductConditions product={product} />

          {!inGeneralState && product.has_variations && (
            <ProductVariantSelector
              product={product}
              selectedVariations={selectedVariations ?? []}
              onSelection={onSelection}
            />
          )}

          {!inGeneralState &&
            product.integration_info?.type === 'warehouse' && (
              <ProductWarehouseSelector
                product={product}
                breadcrumbs={breadcrumbs}
                onSelection={onWarehouseSelection}
              />
            )}

          {!inGeneralState && (
            <div
              className={`${
                isPreview
                  ? 'pointer-events-none relative cursor-not-allowed'
                  : ''
              } contain-layout`}
            >
              {(product.out_of_stock || !product.date_added) &&
              (!product.is_auction || !product.date_added) ? (
                <>
                  <SoldOrDraftBlock
                    variant={
                      product.is_draft && product.seller_id === userDoc?.uid
                        ? 'draft'
                        : 'sold'
                    }
                    product={product}
                  />
                  {(!!userDoc?.roles?.reseller || product.has_variations) &&
                    (product.seller_id === userDoc?.uid ||
                      userDoc?.roles?.admin) && (
                      <Button
                        width="fluid"
                        type="tertiary"
                        text="Edit Listing"
                        href={`/edit-listing/${product.id}`}
                      />
                    )}
                </>
              ) : user === undefined || seller === undefined ? (
                <ActionButtonsSkeleton />
              ) : user?.uid === product.seller_id && !isPreview ? (
                <div className="flex flex-col gap-[1.6rem]">
                  <SellerActionButtons product={product} />
                  <SellerProductOrders product={product} />
                </div>
              ) : (
                (!product.is_draft || isPreview) && (
                  <BuyerActionButtons
                    product={product}
                    seller={seller}
                    selectedVariations={selectedVariations}
                    setErrors={setErrors}
                    onCustomInputsChange={onCustomInputsChange}
                    hideOffer={hideOffer}
                  />
                )
              )}

              {!product.is_draft && product.is_auction && (
                <Elements stripe={stripePromise}>
                  <AuctionActions product={product} />
                </Elements>
              )}
            </div>
          )}
          {mainDisclaimer &&
            !mainDisclaimer.toLowerCase().includes('ai-assisted') &&
            product.category2 !== 'Tires' && (
              <div className="flex items-start gap-x-4 rounded-[2rem] bg-[#F9E8E8] p-[2rem]">
                <DisclaimerIcon className="h-[2rem] w-[2rem] shrink-0 text-[#7F7F7F]" />

                <span className="text-[1.6rem] text-brand-black">
                  {mainDisclaimer}
                </span>
              </div>
            )}

          {!product.is_digital && (
            <>
              <ProductPolicyAccordion
                title={'Shipping & Delivery'}
                icon={
                  <Image
                    src="/icons/shipping-and-delivery.png"
                    alt="Shipping & Delivery"
                    width={72}
                    height={72}
                    className="h-[4rem] w-[4rem] object-contain"
                  />
                }
              >
                <div className="flex w-full flex-col gap-y-2">
                  <div className="flex w-full items-center justify-between">
                    <div className="flex flex-col items-start gap-2">
                      <span className="text-[1.8rem] font-semibold text-black">
                        Delivery
                      </span>

                      {product.shipment?.ship_from.country_code &&
                      getCountryLabelFromCode(
                        product.shipment.ship_from.country_code
                      ) !== 'Unknown' ? (
                        <div className="flex w-full items-center justify-between">
                          <div className="flex items-center gap-x-2">
                            <div className="text-[1.6rem] text-brand-gray">
                              Shipping:
                            </div>
                            <p className="text-[1.6rem] text-brand-gray">
                              Ships from{' '}
                              {product.shipment?.ship_from.state_province},{' '}
                              {getCountryLabelFromCode(
                                product.shipment?.ship_from.country_code ?? ''
                              )}
                              .
                            </p>
                          </div>
                        </div>
                      ) : (
                        product.country_code &&
                        getCountryLabelFromCode(product.country_code ?? '') !==
                          'Unknown' && (
                          <div className="flex w-full items-center justify-between">
                            <div className="flex items-center gap-x-2">
                              <div className="text-[1.6rem] text-brand-gray">
                                Shipping:
                              </div>
                              <p className="text-[1.6rem] text-brand-gray">
                                Ships from{' '}
                                {getCountryLabelFromCode(
                                  product.country_code ?? ''
                                )}
                                .
                              </p>
                            </div>
                          </div>
                        )
                      )}

                      {((userDoc === null && product.country_code === 'US') ||
                        userDoc?.country?.code === 'US') && (
                        <p className="text-[1.6rem] text-brand-gray">
                          Estimated delivery{' '}
                          <span className="font-semibold text-black">
                            {formatRange(shippingDates)}
                          </span>{' '}
                          if you order today!
                          {/* add an astrix if userDoc is null */}
                          {userDoc === null && (
                            <span className="text-[1.6rem] text-brand-gray">
                              *
                            </span>
                          )}
                          {/* explain that the delivery date is for the continental US */}
                          {userDoc === null && (
                            <>
                              <br />
                              <span className="text-[1.2rem] text-brand-gray">
                                *(Continental US only)
                              </span>
                            </>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </ProductPolicyAccordion>

              <ProductPolicyAccordion
                title={'Return Policy'}
                icon={
                  <Image
                    src="/icons/return-policy.png"
                    alt="Return Policy"
                    width={96}
                    height={96}
                    className="h-[4rem] w-[4rem] object-contain"
                  />
                }
              >
                <div className="flex flex-col gap-y-2">
                  <span className="text-[1.8rem] font-semibold text-black">
                    Make Any Order Returnable
                  </span>

                  <p className="text-[1.6rem] text-brand-gray">
                    Want extra peace of mind? Even if a seller doesn&apos;t
                    offer returns, <br />
                    MX Locker gives you the option to make any item returnable
                    with{' '}
                    <span className="font-semibold italic text-zinc-700">
                      Return Assurance
                    </span>{' '}
                    at checkout.
                  </p>
                </div>
              </ProductPolicyAccordion>

              <ProductPolicyAccordion
                title={'Shop With Confidence'}
                icon={
                  <Image
                    src="/icons/shop-with-confidence.png"
                    alt="Shop With Confidence"
                    width={96}
                    height={96}
                    className="h-[4rem] w-[4rem] object-contain"
                  />
                }
              >
                <div className="flex flex-col gap-y-6">
                  <div className="flex flex-col gap-y-2">
                    <span className="text-[1.8rem] font-semibold text-black">
                      MX Locker Buyer Protection Guaranteed
                    </span>

                    <p className="text-[1.6rem] text-brand-gray">
                      MX Locker is 100% committed to ensuring that every sale
                      ends in satisfaction—for both buyer and seller. Your
                      payment is held until the item is delivered and approved.
                      If it&apos;s not as described, you&apos;ll receive a full
                      refund.
                    </p>
                  </div>

                  <div className="h-px w-full bg-zinc-300" />

                  <div className="flex flex-col gap-y-2">
                    <span className="text-[1.8rem] font-semibold text-black">
                      Secure Payment
                    </span>

                    <p className="text-[1.6rem] text-brand-gray">
                      Every transaction is backed by our secure payment system.
                      We hold funds until you confirm the item arrived in the
                      promised condition—so you can shop worry-free.
                    </p>
                  </div>
                </div>
              </ProductPolicyAccordion>
            </>
          )}
          <div className="flex w-full justify-end">
            <Button
              text="Report this item"
              type="text"
              width="small"
              className="!text-[1.6rem] !font-semibold"
              onClick={() => {
                setReportProductModalOpen(true);
              }}
              leadingIcon={
                <FlagIcon
                  fill="white"
                  stroke="#C41719"
                  strokeWidth={2}
                  width={14}
                  height={14}
                />
              }
            />

            <ReportItemModal
              isOpen={reportProductModalOpen}
              itemId={product.id}
              isUser={false}
              dismiss={() => setReportProductModalOpen(false)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const DisclaimerIcon = ({ className }: { className?: string }) => (
  <svg
    width="19"
    height="19"
    viewBox="0 0 19 19"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <title>Disclaimer Icon</title>
    <path
      d="M9.375 0C4.20563 0 0 4.20563 0 9.375C0 14.5444 4.20563 18.75 9.375 18.75C14.5444 18.75 18.75 14.5444 18.75 9.375C18.75 4.20563 14.5444 0 9.375 0ZM9.375 3.84375C9.61605 3.84375 9.85168 3.91523 10.0521 4.04915C10.2525 4.18306 10.4087 4.37341 10.501 4.5961C10.5932 4.8188 10.6174 5.06385 10.5703 5.30027C10.5233 5.53668 10.4072 5.75384 10.2368 5.92429C10.0663 6.09473 9.84918 6.21081 9.61277 6.25783C9.37635 6.30486 9.1313 6.28072 8.9086 6.18848C8.68591 6.09623 8.49556 5.94002 8.36165 5.7396C8.22773 5.53918 8.15625 5.30355 8.15625 5.0625C8.15625 4.73927 8.28465 4.42927 8.51321 4.20071C8.74177 3.97215 9.05177 3.84375 9.375 3.84375ZM12.375 14.4375H6.75V12.9375H8.8125V8.8125H7.3125V7.3125H10.3125V12.9375H12.375V14.4375Z"
      fill="#C41719"
    />
  </svg>
);
